<div class="sagenda">

  <!-- ─── En-tête ──────────────────────────────────────────────────── -->
  <header class="sagenda__head">
    <h1 class="sagenda__title">
      <i class="fa-solid fa-calendar-days"></i>
      {{ 'CALENDAR.TITLE' | translate }}
    </h1>
    <p class="sagenda__sub">{{ 'CALENDAR.STUDENT_SUBTITLE' | translate }}</p>
  </header>

  <!-- ─── Séances du jour ──────────────────────────────────────────── -->
  @if (todaySessions().length) {
    <section class="sagenda-today">
      <h2 class="sagenda-today__title">
        <i class="fa-solid fa-bolt"></i>
        {{ 'CALENDAR.TODAY' | translate }}
      </h2>
      <div class="sagenda-today__list">
        @for (s of todaySessions(); track s.id) {
          <button type="button" class="sagenda-now" [class]="view.colorClass(s)"
                  [class.is-live]="isLive(s)" [class.is-done]="isDone(s)"
                  (click)="selectedEvent.set(s)">
            <span class="sagenda-now__time">{{ time(s.startTime) }}</span>
            <span class="sagenda-now__body">
              <span class="sagenda-now__title">
                {{ s.title }}
                @if (isLive(s)) {
                  <span class="sagenda-now__badge">
                    <span class="sagenda-now__pulse"></span>
                    {{ 'CALENDAR.LIVE_NOW' | translate }}
                  </span>
                }
              </span>
              <span class="sagenda-now__meta">
                @if (s.subjectName) { {{ s.subjectName }} }
                @if (s.location) { · {{ s.location }} }
              </span>
            </span>
            @if (s.meetingUrl) {
              <i class="fa-solid fa-video sagenda-now__cam"></i>
            }
          </button>
        }
      </div>
    </section>
  }

  <!-- ─── Corps ────────────────────────────────────────────────────── -->
  <div class="sagenda__body">

    <section class="sagenda-card">
      <div class="sagenda-toolbar">
        <div class="sagenda-toolbar__nav">
          <button type="button" class="sagenda-nav-btn" (click)="prev()"
                  [attr.aria-label]="'CALENDAR.NAV.PREV' | translate">
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <button type="button" class="sagenda-nav-btn" (click)="next()"
                  [attr.aria-label]="'CALENDAR.NAV.NEXT' | translate">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
          <button type="button" class="sagenda-today-btn" (click)="today()">
            {{ 'CALENDAR.NAV.TODAY' | translate }}
          </button>
        </div>

        <h2 class="sagenda-toolbar__period">{{ periodLabel() }}</h2>

        <div class="sagenda-seg" role="tablist">
          <button type="button" role="tab" class="sagenda-seg__btn"
                  [class.is-active]="activeView() === 'dayGridMonth'"
                  (click)="switchView('dayGridMonth')">
            {{ 'CALENDAR.NAV.MONTH' | translate }}
          </button>
          <button type="button" role="tab" class="sagenda-seg__btn"
                  [class.is-active]="activeView() === 'timeGridWeek'"
                  (click)="switchView('timeGridWeek')">
            {{ 'CALENDAR.NAV.WEEK' | translate }}
          </button>
          <button type="button" role="tab" class="sagenda-seg__btn"
                  [class.is-active]="activeView() === 'timeGridDay'"
                  (click)="switchView('timeGridDay')">
            {{ 'CALENDAR.NAV.DAY' | translate }}
          </button>
        </div>
      </div>

      @if (errored()) {
        <!-- Erreur de chargement : jamais de séances de substitution -->
        <app-error-state titleKey="CALENDAR.ERROR_TITLE"
                         messageKey="CALENDAR.ERROR_DESC"
                         (retry)="reload()"></app-error-state>
      } @else if (!loading() && eventCount() === 0) {
        <app-empty-state titleKey="CALENDAR.EMPTY_TITLE"
                         messageKey="CALENDAR.EMPTY_DESC"></app-empty-state>
      }

      <div class="sagenda-grid"
           [class.is-loading]="loading()"
           [class.is-hidden]="errored()">
        <full-calendar [options]="calendarOptions"></full-calendar>
        @if (loading()) {
          <div class="sagenda-grid__veil"><span class="sagenda-spinner"></span></div>
        }
      </div>
    </section>

    <!-- Panneau « à venir » -->
    <aside class="sagenda-side" aria-labelledby="sagenda-upcoming-title">
      <h3 class="sagenda-side__title" id="sagenda-upcoming-title">
        <i class="fa-solid fa-arrow-trend-up"></i>
        {{ 'CALENDAR.UPCOMING' | translate }}
      </h3>

      @if (upcoming().length === 0) {
        <p class="sagenda-side__empty">{{ 'CALENDAR.UPCOMING_EMPTY' | translate }}</p>
      } @else {
        <ul class="sagenda-side__list">
          @for (s of upcoming(); track s.id) {
            <li>
              <button type="button" class="sagenda-up" [class]="view.colorClass(s)"
                      (click)="selectedEvent.set(s)">
                <span class="sagenda-up__date">
                  <span class="sagenda-up__day">{{ start(s) | date:'dd' }}</span>
                  <span class="sagenda-up__month">{{ start(s) | localizedDate:'MMM' }}</span>
                </span>
                <span class="sagenda-up__body">
                  <span class="sagenda-up__title">{{ s.title }}</span>
                  <span class="sagenda-up__meta">
                    {{ time(s.startTime) }}–{{ time(s.endTime) }}
                    @if (s.subjectName) { · {{ s.subjectName }} }
                  </span>
                </span>
              </button>
            </li>
          }
        </ul>
      }
    </aside>
  </div>

  <!-- ─── Détail d'une séance ──────────────────────────────────────── -->
  @if (selectedEvent(); as s) {
    <div class="sagenda-scrim" (click)="closeDetail()"></div>
    <aside class="sagenda-drawer" [class]="view.colorClass(s)" role="dialog" aria-modal="true"
           aria-labelledby="sagenda-detail-title">
      <button type="button" class="sagenda-drawer__close" (click)="closeDetail()"
              [attr.aria-label]="'CALENDAR.CLOSE' | translate">
        <i class="fa-solid fa-xmark"></i>
      </button>

      <span class="sagenda-drawer__kind">
        <i [class]="view.kindIcon(s.sessionKind)"></i>
        {{ view.kindLabelKey(s.sessionKind) | translate }}
      </span>

      <h2 class="sagenda-drawer__title" id="sagenda-detail-title">{{ s.title }}</h2>

      <ul class="sagenda-drawer__meta">
        <li>
          <i class="fa-solid fa-calendar"></i>
          {{ start(s) | localizedDate:'EEEE dd MMMM yyyy' }}
        </li>
        <li>
          <i class="fa-solid fa-clock"></i>
          {{ time(s.startTime) }} – {{ time(s.endTime) }}
        </li>
        @if (s.subjectName) {
          <li><i class="fa-solid fa-book"></i> {{ s.subjectName }}</li>
        }
        @if (s.consultantName) {
          <li><i class="fa-solid fa-chalkboard-user"></i> {{ s.consultantName }}</li>
        }
        @if (s.groupName) {
          <li><i class="fa-solid fa-users"></i> {{ s.groupName }}</li>
        }
        @if (s.location) {
          <li><i class="fa-solid fa-location-dot"></i> {{ s.location }}</li>
        }
      </ul>

      @if (s.reason) {
        <p class="sagenda-drawer__notes">{{ s.reason }}</p>
      }

      @if (s.meetingUrl) {
        <!-- Passe par l'API : l'accès est journalisé avant d'ouvrir la réunion -->
        <button type="button" class="sagenda-drawer__join" (click)="join(s)">
          <i class="fa-solid fa-video"></i>
          {{ 'CALENDAR.JOIN_MEETING' | translate }}
        </button>
      }
    </aside>
  }

</div>
